import { ActivityIndicator, Image, Pressable, Text, View } from "react-native";
import useFileSelector from "@/models/useFileSelector";
import { useTranslation } from "react-i18next";
import { useRouter } from "@/_router";
import { ms } from "@/ui/moderateScale";
import TextareaAutosize from "react-textarea-autosize";
import Icon, { IconType } from "@/ui/Icon";
import { Camera } from "@capacitor/camera";
import { Colors } from "@/constants/Colors";
import { useUISlice } from "@/models/useUISlice";
import { useState, lazy, Suspense, useEffect } from "react";
import { useUserSlice } from "@/models/useUserSlice";
import * as Types from "@/models/types";
import Menu from "@/ui/Menu";
import { getBasePath, getChatId } from "@/models/utils";
import { useUpload } from "@/models/useUpload";
import { useToast } from "@/models/useToast";
import { AUDIO_LANGUAGES } from "@/constants";
import { formatTime, useRecorder } from "@/models/useRecorder";
import { useAuth } from "@/models/useAuth";
import List from "./List";

// Lazy load the heavy ChatConversation component
const ChatConversation = lazy(() => import("@/ui/ChatConversation"));

// Loading fallback
const ConversationLoading = () => (
  <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
    <Text style={{ fontSize: ms(16), color: Colors.gray8 }}>Loading...</Text>
  </View>
);

function Chat() {
  const router = useRouter();
  const currentAIModel = useUISlice((state) => state.currentAIModel);
  const height = useUISlice((state) => state.device.height);
  const footerHeight = useUISlice((state) => state.footerHeight);
  const width = useUISlice((state) => state.device.width);
  const isAuthenticated = useAuth.isAuthenticated();
  const { t } = useTranslation();
  const chatMessages = useUserSlice((state) => {
    const chatId = getChatId();
    if (!chatId) return [];

    return state.chats.find((chat) => chat.id === chatId)?.messages || [];
  });

  useEffect(() => {
    useUserSlice
      .getState()
      .user()
      .remoteConfig({
        queryParams: { platform: "web" },
      });
    useUserSlice.getState().user().profile();

    // Check if the user is coming from a Stripe checkout success page
    useUserSlice.getState().subscription().onStripeCheckoutSuccess({ router });
  }, []);

  const _onSendMessage = (
    message: string,
    file?: { url: string; contentType: string; name?: string }
  ) => {
    const withSearch = useUISlice.getState().searchEnabled;
    useUserSlice
      .getState()
      .chat()
      .sendMessage({
        message: message.trim(),
        router,
        file,
        aiModelId: withSearch ? currentAIModel.searchModel : currentAIModel.id,
        withSearch,
      });
  };

  if (!currentAIModel) return <View />;

  return (
    <View
      style={{
        flex: 1,
        flexDirection: "row",
        backgroundColor: Colors.white,
        height: height,
        overflow: "hidden",
      }}
    >
      <Chat.Sidebar />

      <View
        style={{
          flex: 1,
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Chat.Header currentAIModel={currentAIModel} />

        <View
          style={{
            flex: 1,
            alignSelf: "center",
            paddingBottom: footerHeight - ms(24),
          }}
        >
          {chatMessages.length > 0 ? (
            <Suspense fallback={<ConversationLoading />}>
              <ChatConversation
                messages={chatMessages}
                scrollViewContentContainerStyle={{
                  paddingHorizontal: (width - Chat.FOOTER_WIDTH - ms(220)) / 2,
                  paddingBottom: ms(100),
                }}
              />
            </Suspense>
          ) : (
            <Chat.EmptyState currentAIModel={currentAIModel} />
          )}
        </View>

        {!isAuthenticated && (
          <Pressable
            onPress={() => useAuth.signIn({})}
            style={{
              marginBottom: footerHeight,
              alignItems: "center",
              justifyContent: "center",
              paddingVertical: ms(16),
              paddingHorizontal: ms(16),
            }}
          >
            <Text
              style={{
                fontSize: ms(16),
                color: Colors.softRed,
                fontWeight: "400",
                textAlign: "center",
                paddingHorizontal: ms(24),
                paddingVertical: ms(12),
                backgroundColor: Colors.softRed + "10",
                borderRadius: ms(8),
              }}
            >
              {t("pageDesktop.notAuthenticated")}{" "}
              <Text
                style={{
                  fontWeight: "600",
                  color: Colors.text,
                  textDecorationLine: "underline",
                }}
              >
                {t("pageDesktop.signInToContinue")}
              </Text>
            </Text>
          </Pressable>
        )}

        <View
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: Colors.white,
            shadowColor: "#fff",
            shadowOffset: {
              width: 0,
              height: -4,
            },
            paddingTop: ms(0),
            shadowOpacity: 0.5,
            shadowRadius: 10,
            elevation: 5,
            zIndex: 10,
          }}
        >
          <Chat.Footer onSendMessage={_onSendMessage} />
        </View>
      </View>
    </View>
  );
}

Chat.HEADER_HEIGHT = ms(60);
Chat.SIDEBAR_WIDTH = ms(300);
Chat.SIDEBAR_COLLAPSED_WIDTH = ms(56);
Chat.FOOTER_WIDTH = ms(870);

Chat.Sidebar = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const chats = useUserSlice((state) => state.chats);
  const chatsNextCursor = useUserSlice((state) => state.chatsNextCursor);
  const height = useUISlice((state) => state.device.height);
  const desktopSidebarOpen = useUISlice((state) => state.desktopSidebarOpen);
  const voiceLanguage = useUserSlice(
    (state) => state?.userData?.transcribeLanguage
  );

  const _onEndReached = async () => {
    if (!chatsNextCursor) return;

    useUserSlice.getState().chat().list({ nextCursor: chatsNextCursor });
  };

  if (!desktopSidebarOpen) {
    return (
      <View
        style={{
          maxWidth: Chat.SIDEBAR_COLLAPSED_WIDTH,
          width: Chat.SIDEBAR_COLLAPSED_WIDTH,
          minWidth: Chat.SIDEBAR_COLLAPSED_WIDTH,
          backgroundColor: "#f0f4f9",
          height: height,
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: ms(20),
        }}
      >
        <View style={{ gap: ms(24), width: "100%", alignItems: "center" }}>
          <Pressable
            onPress={() => useUISlice.getState().setDesktopSidebarOpen(true)}
          >
            <Icon icon="hamburger" style={{ width: ms(24), height: ms(24) }} />
          </Pressable>

          <Pressable
            onPress={() => Chat.newChat(router)}
            style={{
              width: ms(44),
              height: ms(44),
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: Colors.text,
              borderRadius: ms(80),
            }}
          >
            <Icon
              icon="pen"
              style={{
                width: ms(24),
                height: ms(24),
                tintColor: Colors.white,
              }}
            />
          </Pressable>
        </View>

        <Menu
          key={`${voiceLanguage}`}
          actions={[
            {
              text: t("settings.voiceLanguage", {
                language: AUDIO_LANGUAGES.find(
                  (language) => language.value === voiceLanguage
                )?.label,
              }),
              onPress: () =>
                useAuth.signIn({
                  callback: () => {
                    useUserSlice.getState().user().changeTranscribeLanguage();
                  },
                }),
            },
          ]}
          actionComponent={
            <View
              style={{
                width: ms(40),
                height: ms(40),
                alignItems: "center",
                justifyContent: "center",
                paddingBottom: ms(32),
              }}
            >
              <Icon icon="cog" style={{ width: ms(20), height: ms(20) }} />
            </View>
          }
        />
      </View>
    );
  }

  return (
    <View
      style={{
        width: Chat.SIDEBAR_WIDTH,
        maxWidth: Chat.SIDEBAR_WIDTH,
        backgroundColor: "#f0f4f9",
        height: height,
        paddingTop: ms(20),
        justifyContent: "space-between",
      }}
    >
      <View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingHorizontal: ms(16),
          }}
        >
          <Pressable
            onPress={() => useUISlice.getState().setDesktopSidebarOpen(false)}
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon icon="hamburger" style={{ width: ms(24), height: ms(24) }} />
          </Pressable>

          {/* <Pressable>
            <Icon icon="cog" style={{ width: ms(20), height: ms(20) }} />
          </Pressable> */}
        </View>

        <Pressable
          onPress={() => Chat.newChat(router)}
          style={{
            marginHorizontal: ms(16),
            marginVertical: ms(24),
            flexDirection: "row",
            alignItems: "center",
            gap: ms(12),
            height: ms(48),
            paddingHorizontal: ms(12),
            borderRadius: ms(8),
            backgroundColor: Colors.text,
          }}
        >
          <Icon
            icon="pen"
            style={{ width: ms(20), height: ms(20), tintColor: Colors.white }}
          />
          <Text
            style={{ fontSize: ms(16), color: Colors.white, fontWeight: "500" }}
          >
            {t("pageDesktop.newChat")}
          </Text>
        </Pressable>

        <View style={{ flex: 1 }}>
          <Text
            style={{
              fontSize: ms(14),
              color: Colors.text + "80",
              paddingLeft: ms(16),
              fontWeight: "500",
              marginBottom: ms(8),
            }}
          >
            {t("pageDesktop.recent")}
          </Text>

          {chats.length === 0 && (
            <Text
              style={{
                marginTop: ms(4),
                fontSize: ms(14),
                color: Colors.text + "80",
                paddingLeft: ms(16),
                fontWeight: "300",
                marginBottom: ms(12),
              }}
            >
              {t("pageDesktop.emptyChats")}
            </Text>
          )}

          <div
            style={{
              maxHeight: height - ms(240),
              overflow: "hidden",
              overflowY: "auto",
              paddingLeft: ms(16),
              paddingRight: ms(16),
            }}
          >
            <List
              data={chats}
              keyExtractor={(item) => item.id}
              onEndReached={_onEndReached}
              renderItem={({ item }) => <Chat.SideBarRoomItem item={item} />}
            />
          </div>
        </View>
      </View>

      <View style={{ paddingHorizontal: ms(16) }}>
        <Menu
          key={`${voiceLanguage}`}
          actions={[
            {
              text: t("settings.voiceLanguage", {
                language: AUDIO_LANGUAGES.find(
                  (language) => language.value === voiceLanguage
                )?.label,
              }),
              onPress: () =>
                useAuth.signIn({
                  callback: () => {
                    useUserSlice.getState().user().changeTranscribeLanguage();
                  },
                }),
            },
          ]}
          actionComponent={
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: ms(12),
                height: ms(48),
                paddingBottom: ms(32),
              }}
            >
              <Icon
                icon="cog"
                style={{
                  width: ms(20),
                  height: ms(20),
                  tintColor: Colors.gray8,
                }}
              />
              <Text style={{ fontSize: ms(16), color: Colors.gray8 }}>
                {t("pageDesktop.settings")}
              </Text>
            </View>
          }
        />
      </View>
    </View>
  );
};

Chat.SideBarRoomItem = ({ item }: { item: Types.Chat }) => {
  const [hover, setHover] = useState(false);
  const router = useRouter();
  const currentChatId = getChatId();
  const [menuVisible, setMenuVisible] = useState(false);
  const { t } = useTranslation();
  const isSelected = currentChatId === item.id;

  return (
    <Pressable
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      key={item.id}
      onPress={() => router.replace(`${getBasePath()}/?roomId=${item.id}`)}
      style={{
        height: ms(44),
        paddingHorizontal: ms(10),
        marginHorizontal: -ms(10),
        borderRadius: ms(8),
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        ...((hover || isSelected || menuVisible) && {
          backgroundColor: Colors.text + "10",
        }),
      }}
    >
      <Text
        numberOfLines={1}
        style={{
          fontSize: ms(16),
          color: isSelected ? Colors.text : Colors.gray8,
        }}
      >
        {item.title}
      </Text>

      <Menu
        onPopupVisibleChange={(visible) => setMenuVisible(visible)}
        key={`${Boolean(hover || menuVisible)}-${Boolean(isSelected)}`}
        actions={[
          {
            text: t("pageDesktop.rename"),
            onPress: () => useUserSlice.getState().chat().rename(item.id),
          },
          {
            text: t("pageDesktop.delete"),
            destructive: true,
            onPress: () => {
              useUserSlice
                .getState()
                .chat()
                .delete({
                  chatId: item.id,
                  callback: () => {
                    router.replace(`${getBasePath()}/`);
                  },
                });
            },
          },
        ]}
        actionComponent={
          <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
              display: "none",
              width: ms(40),
              height: ms(40),
              marginRight: -ms(8),
              alignItems: "center",
              justifyContent: "center",
              ...((isSelected || hover || menuVisible) && {
                display: "flex",
              }),
            }}
          >
            <Icon
              icon="moreHorizontal"
              style={{ width: ms(20), height: ms(20), tintColor: Colors.gray8 }}
            />
          </div>
        }
      />
    </Pressable>
  );
};

Chat.Header = ({ currentAIModel }: { currentAIModel: Types.AIModel }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const isAuthenticated = useAuth.isAuthenticated();
  const user = useUserSlice((state) => state.userData);

  return (
    <View
      style={{
        height: Chat.HEADER_HEIGHT,
        backgroundColor: Colors.white,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: ms(16),
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: ms(16) }}>
        <Pressable
          onPress={() => useUISlice.setState({ alertWidget: "selectAIModel" })}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: ms(8),
            height: ms(44),
            paddingHorizontal: ms(20),
            borderRadius: ms(30),
            backgroundColor: Colors.accent + "10",
          }}
        >
          <Icon
            icon={currentAIModel?.icon as IconType}
            style={{ width: ms(20), height: ms(20) }}
          />
          <Text
            style={{ fontSize: ms(16), fontWeight: "500", color: Colors.text }}
          >
            {currentAIModel?.name}
          </Text>
          <Text style={{ fontSize: ms(16), color: Colors.text + "80" }}>
            {currentAIModel?.model}
          </Text>
          <Icon
            icon="caretDown"
            style={{
              width: ms(20),
              height: ms(20),
              tintColor: Colors.text + "80",
            }}
          />
        </Pressable>
      </View>

      {isAuthenticated && (
        <View
          style={{ flexDirection: "row", alignItems: "center", gap: ms(12) }}
        >
          {!isSubscribed && (
            <Pressable
              onPress={() => useUISlice.getState().setPaywallVisible(true)}
              style={{
                flexDirection: "row",
                alignItems: "center",
                height: ms(44),
                paddingHorizontal: ms(16),
                borderRadius: ms(30),
                backgroundColor: Colors.accent + "10",
              }}
            >
              <Text
                style={{
                  fontSize: ms(16),
                  color: Colors.accent,
                  fontWeight: "500",
                }}
              >
                {t("pageDesktop.upgrade")}
              </Text>
            </Pressable>
          )}

          <Menu
            key={`${isAuthenticated}-${user?.email}-${isSubscribed}`}
            actions={[
              isSubscribed && {
                text: t("pageDesktop.manageSubscription"),
                onPress: () =>
                  useUserSlice
                    .getState()
                    .subscription()
                    .stripeManageSubscription(),
              },
              {
                text: t("pageDesktop.contactSupport"),
                onPress: useUserSlice.getState().user().contactSupport,
              },
              {
                destructive: true,
                text: t("pageDesktop.signOut"),
                onPress: () => useAuth.signOut(),
              },
            ].filter(Boolean)}
            actionComponent={
              <View
                style={{
                  width: ms(40),
                  height: ms(40),
                  borderRadius: ms(20),
                  backgroundColor: "#00897b",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    fontSize: ms(16),
                    color: Colors.white,
                    fontWeight: "500",
                  }}
                >
                  {user?.email?.charAt(0)?.toUpperCase()}
                </Text>
              </View>
            }
          />
        </View>
      )}
    </View>
  );
};

Chat.EmptyState = ({ currentAIModel }: { currentAIModel: Types.AIModel }) => {
  const { t } = useTranslation();

  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        gap: ms(12),
        justifyContent: "center",
        width: "100%",
      }}
    >
      <Icon
        icon={currentAIModel?.icon as IconType}
        style={{ width: ms(48), height: ms(48) }}
      />
      <Text
        style={{
          fontSize: ms(18),
          fontWeight: "500",
          textAlign: "center",
          marginTop: ms(12),
        }}
      >
        {t("chat.hi", { name: currentAIModel?.name })}{" "}
        <Text style={{ fontWeight: "400", color: Colors.text + "80" }}>
          {currentAIModel?.model}
        </Text>
      </Text>

      <Text
        style={{
          fontSize: ms(16),
          fontWeight: "400",
          color: Colors.text + "80",
        }}
      >
        {t("chat.description")}
      </Text>
    </View>
  );
};

Chat.Footer = ({
  onSendMessage,
}: {
  onSendMessage: (
    message: string,
    file?: { url: string; contentType: string; name?: string }
  ) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const searchEnabled = useUISlice((state) => state.searchEnabled);
  const { toast } = useToast();
  const isSubscribed = useUserSlice((state) => state.isSubscribed);
  const footerHeight = useUISlice((state) => state.footerHeight);
  const [transcribing, setTranscribing] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const freeAICreditLeft = Math.max(
    0,
    (useUserSlice.getState().remoteConfig?.dailyMessageLimit || 0) -
      (useUserSlice.getState().userData?.dailyMessagesCount?.[today] || 0)
  );
  const { openFilePicker } = useFileSelector();
  const [file, setFile] = useState<
    { url: string; contentType: string; name?: string } | undefined
  >(undefined);
  const { uploadFile, uploadProgress, isUploading, setUploadParams } =
    useUpload({
      maxFileSize: 5 * 1024 * 1024 * 1024, // 5GB
    });

  const {
    status,
    elapsedTime,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useRecorder();

  const _onSendMessage = () => {
    if (!useAuth.isAuthenticated()) {
      useAuth.signIn({});
      return;
    }

    setMessage("");
    onSendMessage(message, file);
    setFile(undefined);
    useUISlice.getState().setFooterHeight("none");
  };

  const _getUploadedFileUrl = async (
    file: File
  ): Promise<string | undefined> => {
    if (!file) return;

    const fileExtension = file?.name?.split(".").pop() || "";
    setUploadParams({ fileType: file?.type, fileExtension });

    useUISlice.getState().setFooterHeight("uploading");
    const uploadResponse = await uploadFile(file);

    if (!uploadResponse.success) return;

    return uploadResponse.downloadUrl;
  };

  const _updateFile = (url: string, contentType: string, name?: string) => {
    useUISlice.getState().setFooterHeight(contentType);
    setFile({ url, contentType, name });
  };

  const _onSelectImage = async () => {
    const result = await Camera.pickImages({
      quality: 90,
      limit: 1,
    });

    const item = result?.photos?.[0];
    if (!item) return;

    const response = await fetch(item.webPath);
    const blob = await response.blob();
    const type = item.format === "jpeg" ? "image/jpeg" : "image/png";
    const extension = item.format === "jpeg" ? ".jpg" : ".png";
    _updateFile(item.webPath, type);
    const imageUrl = await _getUploadedFileUrl(
      new File([blob], `${new Date().getTime()}${extension}`, { type })
    );

    if (imageUrl) {
      _updateFile(imageUrl, type);
    }
  };

  const _onSelectFile = async () => {
    const supportedFileTypes = [".pdf"];
    const file = await openFilePicker(supportedFileTypes);

    if (!file) return;

    const fileExtension = file?.name?.split(".").pop();

    if (!supportedFileTypes.includes(`.${fileExtension}`)) {
      toast.error(t("chat.footerOnlyPDFFilesSupported"));
      return;
    }

    const fileUrl = await _getUploadedFileUrl(file);
    if (fileUrl) {
      _updateFile(fileUrl, file.type, file.name);
    }
  };

  const _onRemoveFile = () => {
    setFile(undefined);
    useUISlice.getState().setFooterHeight("none");
  };

  const _onToggleSearch = () => {
    if (!isSubscribed) {
      useUISlice.getState().setPaywallVisible(true);
      return;
    }

    useUISlice.getState().setSearchEnabled(!searchEnabled);
  };

  const _onStartRecording = () => {
    if (!isSubscribed) {
      useUISlice.getState().setPaywallVisible(true);
      return;
    }

    const transcribeLanguage =
      useUserSlice.getState().userData?.transcribeLanguage;

    if (transcribeLanguage) {
      startRecording();
      return;
    }

    useUISlice.getState().setAlertSelect({
      title: t("chat.selectLanguage"),
      options: AUDIO_LANGUAGES.map((language) => language.label),
      defaultOption: AUDIO_LANGUAGES.findIndex(
        (language) => language.value === transcribeLanguage
      ),
      onSelect: (index) => {
        const languageCode = AUDIO_LANGUAGES[index].value;
        useUserSlice
          .getState()
          .user()
          .update({
            transcribeLanguage:
              languageCode?.slice(0, 2)?.toLowerCase() || "en",
          });
        startRecording();
      },
    });
  };

  const _onStopRecording = async () => {
    setTranscribing(true);

    // use recorder
    await (stopRecording as ReturnType<typeof useRecorder>["stopRecording"])(
      async (file) => {
        const audioUrl = await _getUploadedFileUrl(file);
        if (!audioUrl) return;

        const transcript = await useUserSlice.getState().chat().transcribe({
          fileUrl: audioUrl,
          languageCode: useUserSlice.getState().userData?.transcribeLanguage,
        });

        setMessage(transcript);
        setTranscribing(false);
      }
    );
  };

  const _onCancelRecording = () => {
    cancelRecording(() => {});
  };

  return (
    <View
      style={{
        backgroundColor: Colors.white,
        maxHeight: footerHeight + ms(16),
        paddingHorizontal: ms(12),
        paddingBottom: ms(20),
        width: "100%",
        maxWidth: Chat.FOOTER_WIDTH,
        alignSelf: "center",
      }}
    >
      <View
        style={{
          borderRadius: ms(24),
          borderWidth: 1,
          borderColor: Colors.text + "40",
          paddingTop: file || (isUploading && !transcribing) ? ms(12) : ms(2),
          paddingLeft: ms(16),
          paddingRight: ms(16),
          paddingBottom: ms(12),
        }}
      >
        {isUploading && !transcribing && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: ms(8),
              width: Chat.ImageSize - ms(12),
              height: Chat.ImageSize - ms(12),
              borderRadius: ms(12),
              backgroundColor: Colors.gray6,
              justifyContent: "center",
            }}
          >
            <ActivityIndicator size="small" color={Colors.white} />
          </View>
        )}
        {file?.contentType?.includes("image") && !isUploading && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              marginBottom: ms(8),
              width: Chat.ImageSize - ms(12),
              height: Chat.ImageSize - ms(12),
              borderRadius: ms(12),
              backgroundColor: Colors.gray6,
              justifyContent: "center",
            }}
          >
            <View style={{ position: "relative" }}>
              <Image
                source={{ uri: file.url }}
                style={{
                  width: Chat.ImageSize - ms(12),
                  height: Chat.ImageSize - ms(12),
                  borderRadius: ms(12),
                }}
              />

              <Pressable
                onPress={_onRemoveFile}
                style={{
                  position: "absolute",
                  top: ms(4),
                  right: ms(4),
                  backgroundColor: Colors.white,
                  borderRadius: ms(2),
                  shadowColor: Colors.text + "08",
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.25,
                  shadowRadius: 3.84,
                  elevation: 5,
                }}
              >
                <Icon
                  icon="dismiss"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    borderRadius: ms(10),
                    padding: ms(4),
                  }}
                />
              </Pressable>
            </View>
          </View>
        )}

        {file?.url?.toLowerCase()?.endsWith(".pdf") && !isUploading && (
          <View style={{ position: "relative", width: "80%" }}>
            <View
              style={{
                height: ms(60),
                backgroundColor: Colors.text + "20",
                borderRadius: ms(16),
                flexDirection: "row",
                gap: ms(12),
                alignItems: "center",
                paddingHorizontal: ms(12),
              }}
            >
              <Icon
                icon="document"
                containerStyle={{
                  backgroundColor: Colors.text,
                  padding: ms(6),
                  borderRadius: ms(8),
                  alignItems: "center",
                }}
                style={{
                  width: ms(24),
                  height: ms(24),
                  tintColor: Colors.white,
                }}
              />

              <View style={{ flex: 1, gap: ms(4) }}>
                <Text
                  numberOfLines={1}
                  style={{
                    fontSize: ms(14),
                    fontWeight: "500",
                    color: Colors.text,
                  }}
                >
                  {file.name}
                </Text>

                <Text
                  style={{
                    fontSize: ms(12),
                    fontWeight: "400",
                    color: Colors.text + "80",
                  }}
                >
                  {isUploading ? t("chat.uploading") : t("chat.uploaded")}
                </Text>
              </View>
            </View>

            <Pressable
              onPress={_onRemoveFile}
              style={{
                position: "absolute",
                top: ms(8),
                right: ms(8),
                width: ms(20),
                height: ms(20),
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: Colors.text,
                borderRadius: ms(12),
                shadowColor: Colors.text + "08",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.25,
                shadowRadius: 3.84,
                elevation: 5,
              }}
            >
              <Icon
                icon="dismiss"
                style={{
                  width: ms(16),
                  height: ms(16),
                  tintColor: Colors.white,
                }}
              />
            </Pressable>
          </View>
        )}

        <TextareaAutosize
          placeholder={t("chat.footerPlaceholder")}
          autoFocus={true}
          style={{
            fontSize: ms(18),
            color: Colors.text,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontWeight: "400",
            paddingTop: ms(16),
            paddingBottom: ms(20),
            outline: "none",
            resize: "none",
            borderWidth: 0,
            backgroundColor: "transparent",
          }}
          minRows={1}
          maxRows={2}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        {transcribing && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: ms(12),
              height: ms(32),
              paddingLeft: ms(4),
            }}
          >
            <ActivityIndicator size="small" color={Colors.text} />
            <Text
              style={{
                fontSize: ms(16),
                fontWeight: "400",
                color: Colors.gray8,
              }}
            >
              {t("chat.convertingToText")}
            </Text>
          </View>
        )}

        {!transcribing && status === "recording" && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: ms(12),
              }}
            >
              <Pressable
                onPress={_onCancelRecording}
                style={{
                  width: ms(32),
                  height: ms(32),
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon="dismiss"
                  style={{ width: ms(24), height: ms(24) }}
                />
              </Pressable>

              <Text
                style={{
                  fontSize: ms(14),
                  fontWeight: "500",
                  color: Colors.text,
                }}
              >
                {formatTime(elapsedTime)}
              </Text>
            </View>

            <Pressable
              onPress={_onStopRecording}
              style={{
                width: ms(40),
                height: ms(40),
                borderRadius: ms(24),
                backgroundColor: Colors.accent,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon
                icon="tick"
                style={{
                  width: ms(20),
                  height: ms(20),
                  tintColor: Colors.white,
                }}
              />
            </Pressable>
          </View>
        )}

        {!transcribing && status !== "recording" && (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: ms(4),
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: ms(12),
              }}
            >
              <Menu
                actions={[
                  {
                    icon: "picture",
                    onPress: () => useAuth.signIn({ callback: _onSelectImage }),
                    text: t("chat.footerAddPhoto"),
                  },
                  {
                    icon: "fileAdd",
                    onPress: () => useAuth.signIn({ callback: _onSelectFile }),
                    text: t("chat.footerAddFiles"),
                  },
                ]}
                actionComponent={
                  <Icon
                    icon="plus"
                    style={{
                      width: ms(24),
                      height: ms(24),
                      tintColor: Colors.text,
                    }}
                  />
                }
              />

              <Pressable
                onPress={_onToggleSearch}
                style={{
                  height: ms(40),
                  paddingHorizontal: ms(12),
                  borderRadius: ms(30),
                  flexDirection: "row",
                  backgroundColor: Colors.text + "08",
                  alignItems: "center",
                  gap: ms(4),
                  ...(searchEnabled && {
                    backgroundColor: Colors.accent + "10",
                  }),
                }}
              >
                <Icon
                  icon="world"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    tintColor: Colors.text,
                    ...(searchEnabled && {
                      tintColor: Colors.accent,
                    }),
                  }}
                />
                <Text
                  style={{
                    fontSize: ms(14),
                    fontWeight: "500",
                    color: Colors.gray8,
                    ...(searchEnabled && {
                      color: Colors.accent,
                    }),
                  }}
                >
                  {t("chat.footerSearch")}
                </Text>
              </Pressable>
            </View>

            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: ms(16),
              }}
            >
              <Pressable
                onPress={_onStartRecording}
                style={{
                  width: ms(40),
                  height: ms(40),
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon="mic"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    tintColor: Colors.text,
                  }}
                />
              </Pressable>

              <Pressable
                disabled={message.length === 0}
                onPress={_onSendMessage}
                style={{
                  width: ms(40),
                  height: ms(40),
                  borderRadius: ms(24),
                  backgroundColor:
                    message.length === 0 ? Colors.accent + "30" : Colors.accent,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon
                  icon="send"
                  style={{
                    width: ms(20),
                    height: ms(20),
                    tintColor: Colors.white,
                  }}
                />

                {!isSubscribed && freeAICreditLeft < 5 && (
                  <Text
                    style={{
                      position: "absolute",
                      top: -ms(8),
                      right: -ms(4),
                      fontSize: ms(10),
                      fontWeight: "600",
                      color: Colors.white,
                      backgroundColor: Colors.text,
                      paddingHorizontal: ms(6),
                      paddingVertical: ms(3),
                      borderRadius: ms(12),
                    }}
                  >
                    {freeAICreditLeft}
                  </Text>
                )}
              </Pressable>
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

Chat.newChat = (router: ReturnType<typeof useRouter>) => {
  useAuth.signIn({
    callback: () => {
      router.replace("/");
    },
  });
};

Chat.ImageSize = ms(90);

export default Chat;
